import React, { useEffect, useState } from 'react';
import { ArticleCategory } from '../../types';
import { formatArticleCount, toFaDigits } from '../../utils/blogFormat';
import BlogIcon from './BlogIcon';
import { useBlogSettings } from './blogSettings';

export type BlogSort = 'newest' | 'popular';

const SORT_LABELS: Record<BlogSort, string> = { newest: 'جدیدترین', popular: 'پربازدیدترین' };

/* ───────────── نوار جستجو ───────────── */
interface BlogSearchBarProps {
  value: string;
  onSubmit: (value: string) => void;
  onClear: () => void;
}

export const BlogSearchBar: React.FC<BlogSearchBarProps> = ({ value, onSubmit, onClear }) => {
  const { searchPlaceholder } = useBlogSettings();
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  return (
    <form
      role="search"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(draft.trim());
      }}
      className="relative flex h-12 w-full items-center rounded-[12px] border border-[rgba(205,195,208,0.5)] bg-white sm:h-14 sm:gap-2 sm:border-[#e9e8e5] sm:px-[10px] sm:py-[6px] sm:drop-shadow-[0px_1px_1.5px_rgba(30,32,34,0.04)]"
    >
      <label htmlFor="blog-search" className="sr-only">
        جست‌وجو در مقالات
      </label>
      <div className="relative h-full min-w-0 flex-1">
        {/* جای آیکون‌ها دقیقاً طبق طرح (نه وسط‌چین) */}
        <span className="pointer-events-none absolute right-[11px] top-[13px] flex sm:right-3 sm:top-[7px]">
          <BlogIcon name="search" width={15} height={15} />
        </span>
        <input
          id="blog-search"
          type="search"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={searchPlaceholder}
          autoComplete="off"
          className="h-full w-full bg-transparent pl-14 pr-10 text-[16px] text-[#1a1c1a] placeholder:text-[14px] placeholder:text-[rgba(91,95,102,0.7)] focus:outline-none sm:pl-8 sm:pr-[44px] sm:text-[16px] sm:placeholder:text-[16px] sm:placeholder:text-[#52575e]"
        />
        {/* طبق طرح: دسکتاپ دکمهٔ پاک‌کردن همیشه دیده می‌شود، موبایل ندارد */}
        <button
          type="button"
          onClick={() => {
            setDraft('');
            onClear();
          }}
          aria-label="پاک‌کردن جست‌وجو"
          className="absolute left-1 top-[3px] hidden items-center justify-center rounded-[4px] p-1 hover:bg-[#f4f3f1] sm:flex"
        >
          <BlogIcon name="close" width={10.5} height={10.5} />
        </button>
      </div>
      <button
        type="submit"
        className="absolute left-[5px] top-[5px] h-9 shrink-0 rounded-[8px] bg-[#4a2e68] px-3 text-[12px] font-medium leading-5 text-white drop-shadow-[0px_1px_1px_rgba(0,0,0,0.05)] transition-colors hover:bg-[#392352] sm:static sm:h-11 sm:bg-[#331751] sm:px-6 sm:text-[14px] sm:font-semibold"
      >
        جست‌وجو
      </button>
    </form>
  );
};

/* ───────────── چیپ‌های دسته‌بندی ───────────── */
interface CategoryChipsProps {
  categories: ArticleCategory[];
  activeSlug: string | null;
  onSelect: (slug: string | null) => void;
}

export const CategoryChips: React.FC<CategoryChipsProps> = ({ categories, activeSlug, onSelect }) => {
  const chip = (label: string, active: boolean, onClick: () => void, key: string) => (
    <button
      key={key}
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex h-9 shrink-0 items-center gap-[6px] whitespace-nowrap rounded-full px-4 text-[12px] font-medium leading-5 transition-colors sm:h-auto sm:border sm:py-[6px] sm:text-[14px] ${
        active
          ? 'bg-[#4a2e68] text-white drop-shadow-[0px_1px_1px_rgba(0,0,0,0.05)] sm:border-[#331751] sm:bg-[#331751]'
          : 'bg-[#efeeeb] text-[#5b5f66] hover:bg-[#e9e8e5] sm:border-[#e9e8e5] sm:bg-[#f4f3f1] sm:text-[#52575e]'
      }`}
    >
      {label}
      {active && <span className="hidden h-[6px] w-[6px] rounded-full bg-[#efdbff] sm:inline-block" aria-hidden />}
    </button>
  );

  return (
    <div className="blog-scroll-x -mx-4 flex items-center gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0" role="group" aria-label="دسته‌بندی موضوعی">
      {chip('همه مقالات', !activeSlug, () => onSelect(null), 'all')}
      {categories.map((c) => chip(c.name, activeSlug === c.slug, () => onSelect(activeSlug === c.slug ? null : c.slug), c.id))}
    </div>
  );
};

/* ───────────── هدر «همه مقالات» + مرتب‌سازی + فیلتر ویدیو ───────────── */
interface BlogToolbarProps {
  total: number;
  sort: BlogSort;
  onSortChange: (sort: BlogSort) => void;
  hasVideo: boolean;
  onHasVideoChange: (value: boolean) => void;
  /** عبارت جستجو (برای عنوان «نتایج») */
  query?: string;
}

export const BlogToolbar: React.FC<BlogToolbarProps> = ({ total, sort, onSortChange, hasVideo, onHasVideoChange, query }) => {
  const { allArticlesTitle } = useBlogSettings();
  const heading = query ? 'نتایج جست‌وجو' : allArticlesTitle;
  return (
    <div className="flex flex-col pt-8 lg:mt-6 lg:flex-row lg:items-center lg:justify-between lg:border-t lg:border-[#e9e8e5] lg:pt-5">
      {/* عنوان + شمارنده */}
      <div className="flex items-center gap-[6px] border-b border-[#e9e8e5] pb-3 lg:gap-[10px] lg:border-0 lg:pb-0">
        <span className="h-4 w-[6px] rounded-full bg-[#4a2e68] lg:hidden" aria-hidden />
        <h2 className="text-[18px] font-bold leading-[30px] text-[#1a1c1a] lg:text-[24px] lg:leading-8">{heading}</h2>
        <span className="text-[14px] leading-[26px] text-[#5b5f66] lg:hidden">({formatArticleCount(total)})</span>
        <span className="hidden rounded-full bg-[#efeeeb] px-[10px] py-[2px] text-[12px] font-medium leading-4 text-[#52575e] lg:inline-block">
          {formatArticleCount(total)}
        </span>
      </div>

      {/* کنترل‌ها */}
      <div className="flex flex-wrap items-center justify-between gap-y-1 pt-3 lg:flex-nowrap lg:gap-4 lg:pt-0">
        {/* دسکتاپ: دکمه‌های مرتب‌سازی (طبق طرح در منتهی‌الیه چپ) */}
        <div className="hidden items-center gap-[6px] lg:order-3 lg:flex">
          <BlogIcon name="sort" width={12} height={8} />
          <span className="pl-1 text-[14px] leading-5 text-[#52575e]">مرتب‌سازی:</span>
          {(['newest', 'popular'] as BlogSort[]).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => onSortChange(key)}
              aria-pressed={sort === key}
              className={`min-h-9 rounded-[6px] px-3 py-2 text-[14px] leading-5 transition-colors ${
                sort === key ? 'bg-[#efeeeb] font-bold text-[#331751]' : 'text-[#52575e] hover:bg-[#f4f3f1]'
              }`}
            >
              {SORT_LABELS[key]}
            </button>
          ))}
        </div>
        <span className="hidden h-4 w-px bg-[#e9e8e5] lg:order-2 lg:block" aria-hidden />

        {/* موبایل: دراپ‌داون مرتب‌سازی */}
        <label className="relative flex h-10 shrink-0 items-center whitespace-nowrap rounded-[8px] border border-[#e9e8e5] bg-white pl-[24px] pr-[9px] text-[12px] font-medium leading-5 text-[#1a1c1a] drop-shadow-[0px_1px_1px_rgba(0,0,0,0.05)] lg:hidden">
          <BlogIcon name="sort-select" width={18} height={18} className="ml-1" />
          <span className="pointer-events-none">مرتب‌سازی: {SORT_LABELS[sort]}</span>
          <span className="pointer-events-none absolute left-[12px] top-1/2 -translate-y-1/2">
            <BlogIcon name="chevron-down-xs" width={9} height={5.55} />
          </span>
          <select
            aria-label="مرتب‌سازی"
            value={sort}
            onChange={(e) => onSortChange(e.target.value as BlogSort)}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          >
            <option value="newest">جدیدترین</option>
            <option value="popular">پربازدیدترین</option>
          </select>
        </label>

        {/* فیلتر ویدیو */}
        <label className="flex min-h-[44px] cursor-pointer select-none items-center gap-[6px] whitespace-nowrap rounded-[8px] px-[10px] py-3 text-[12px] font-medium leading-5 text-[#5b5f66] lg:order-1 lg:gap-2 lg:px-1 lg:text-[14px] lg:text-[#52575e]">
          <input
            type="checkbox"
            checked={hasVideo}
            onChange={(e) => onHasVideoChange(e.target.checked)}
            className="h-4 w-4 shrink-0 cursor-pointer appearance-none rounded-[4px] border border-[#cdc3d0] bg-white transition-colors checked:border-[#4a2e68] checked:bg-[#4a2e68] checked:shadow-[inset_0_0_0_3px_#ffffff] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#4a2e68] lg:border-[#e9e8e5]"
          />
          فقط مقالات دارای ویدیو
        </label>
      </div>
    </div>
  );
};

/* ───────────── نوار فیلترهای فعال + شمارندهٔ نتایج ───────────── */
interface ActiveFiltersBarProps {
  query?: string;
  categoryName?: string | null;
  tag?: string | null;
  hasVideo?: boolean;
  total: number;
  sort: BlogSort;
  onClear: () => void;
}

export const ActiveFiltersBar: React.FC<ActiveFiltersBarProps> = ({ query, categoryName, tag, hasVideo, total, sort, onClear }) => {
  const chips: string[] = [];
  if (categoryName) chips.push(`دسته‌بندی: ${categoryName}`);
  if (tag) chips.push(`برچسب: ${tag}`);
  if (hasVideo) chips.push('دارای ویدیو');

  return (
    <div className="flex flex-col gap-3">
      {chips.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-[8px] border border-[#f0eeea] bg-[#f4f3f1] p-3">
          <div className="flex flex-wrap items-center gap-[6px]">
            <span className="text-[12px] leading-4 text-[#666]">فیلترهای فعال:</span>
            {chips.map((chip) => (
              <span key={chip} className="rounded-[4px] border border-[#e6e4df] bg-white px-2 py-[2px] text-[12px] font-medium leading-4 text-[#222]">
                {chip}
              </span>
            ))}
          </div>
          <button type="button" onClick={onClear} className="text-[12px] font-bold leading-4 text-[#4a2e68] hover:text-[#392352]">
            پاک‌کردن فیلترها
          </button>
        </div>
      )}
      {total > 0 && (
      <div className="flex flex-wrap items-center justify-between gap-2 px-1 pt-1 text-[12px] leading-4">
        <p className="text-[#666]">
          {query ? (
            <>
              نمایش نتایج برای «{query}»:{' '}
              <strong className="font-bold text-[#222]">{toFaDigits(total)} مقاله یافت شد</strong>
            </>
          ) : (
            <strong className="font-bold text-[#222]">{toFaDigits(total)} مقاله یافت شد</strong>
          )}
        </p>
        <span className="text-[#666]">مرتب‌سازی: {SORT_LABELS[sort]}</span>
      </div>
      )}
    </div>
  );
};
